import {createAsyncThunk} from "@reduxjs/toolkit";
import extendedSearchApi from "api/extendedSearchApi";
import projectApi from "api/projectApi";
import {
	TExtendedSearchAbortResponse,
	TExtendedSearchErrorBody,
	TExtendedSearchFoundUrlsQuery,
	TExtendedSearchFoundUrlsResponse,
	TExtendedSearchImportProject,
	TExtendedSearchImportResponse,
	TExtendedSearchLimits,
	TExtendedSearchProjectCompaniesResponse,
	TExtendedSearchProjectMatch,
	TExtendedSearchRunCreate,
	TExtendedSearchRunCreated,
	TExtendedSearchRunDetail,
	TExtendedSearchRunListItem,
} from "api/extendedSearchApi/types";
import {AxiosResponse} from "axios";
import {TExtendedSearchError, TStartImportArgs} from "store/extended-search/types";

type TThunkConfig = {rejectValue: TExtendedSearchError};

/**
 * One page of matches is the whole feature: the operator is picking one project, and a list long
 * enough to scroll means the search was too vague to pick from anyway.
 */
const FIRST_PROJECT_PAGE = 1;
const PROJECT_MATCH_LIMIT = 10;

/**
 * HttpClient resolves a failed call with the error response — or with `undefined` when the request
 * never reached the server — instead of throwing, so each thunk classifies the response itself.
 */
export const isFailure = (response: AxiosResponse | undefined): boolean =>
	!response || response.status >= 400;

/**
 * Reads the server's own wording out of a refusal. Connexion renders werkzeug HTTP exceptions as
 * RFC-7807 problem documents (`detail`), while the spec's ConflictResponse/BadRequestResponse
 * declare `message`, so both are read. The fallback is only ever used when the server said nothing
 * at all: an operator-facing refusal — above all the import 409 that names the run holding the
 * gate — has to reach the UI intact rather than becoming "something went wrong".
 */
export const errorFrom = (
	response: AxiosResponse | undefined,
	fallbackMessage: string,
): TExtendedSearchError => {
	const body = response?.data as TExtendedSearchErrorBody | undefined;

	return {
		status: response?.status ?? null,
		message: body?.detail || body?.message || fallbackMessage,
	};
};

export const fetchExtendedSearchLimits = createAsyncThunk<
	TExtendedSearchLimits,
	void,
	TThunkConfig
>("extendedSearch/fetchLimits", async (_: void, {rejectWithValue}) => {
	const response = await extendedSearchApi.getLimits();

	if (isFailure(response)) {
		return rejectWithValue(errorFrom(response, "Could not load the Extended Search limits"));
	}

	return response.data;
});

export const fetchExtendedSearchRuns = createAsyncThunk<
	TExtendedSearchRunListItem[],
	void,
	TThunkConfig
>("extendedSearch/fetchRuns", async (_: void, {rejectWithValue}) => {
	const response = await extendedSearchApi.listRuns();

	if (isFailure(response)) {
		return rejectWithValue(errorFrom(response, "Could not load the Extended Search runs"));
	}

	return response.data?.results ?? [];
});

export const fetchExtendedSearchRun = createAsyncThunk<
	TExtendedSearchRunDetail,
	number,
	TThunkConfig
>("extendedSearch/fetchRun", async (extendedSearchRunId, {rejectWithValue}) => {
	const response = await extendedSearchApi.getRun(extendedSearchRunId);

	if (isFailure(response)) {
		return rejectWithValue(errorFrom(response, "Could not load this Extended Search run"));
	}

	// Returned whole, `error_message` and per-company rows included — the page reads the run's own
	// explanation from here after an import that lost the gate.
	return response.data;
});

export type TFetchFoundUrlsArgs = TExtendedSearchFoundUrlsQuery & {extendedSearchRunId: number};

/**
 * One page of a run's discovered URLs. Never dispatched by the run-list poll: a run may hold up to
 * `max_total_results_per_run` of them, so this is fetched only when an operator opens the list, and
 * again when they page or change the status filter.
 */
export const fetchExtendedSearchFoundUrls = createAsyncThunk<
	TExtendedSearchFoundUrlsResponse,
	TFetchFoundUrlsArgs,
	TThunkConfig
>(
	"extendedSearch/fetchFoundUrls",
	async ({extendedSearchRunId, page, status}, {rejectWithValue}) => {
		const response = await extendedSearchApi.getFoundUrls(extendedSearchRunId, {page, status});

		if (isFailure(response)) {
			return rejectWithValue(
				errorFrom(response, "Could not load the discovered profile URLs"),
			);
		}

		return response.data;
	},
);

export const createExtendedSearchRun = createAsyncThunk<
	TExtendedSearchRunCreated,
	TExtendedSearchRunCreate,
	TThunkConfig
>("extendedSearch/createRun", async (body, {dispatch, rejectWithValue}) => {
	const response = await extendedSearchApi.createRun(body);

	if (isFailure(response)) {
		// A 400 here is the server refusing a run that breaks a size limit, and it says which limit —
		// the form shows that message verbatim.
		return rejectWithValue(errorFrom(response, "Could not start the Extended Search run"));
	}

	// The new run has to show up in the table without waiting for the next poll.
	dispatch(fetchExtendedSearchRuns());

	return response.data;
});

export const startExtendedSearchRunImport = createAsyncThunk<
	TExtendedSearchImportResponse,
	TStartImportArgs,
	TThunkConfig
>("extendedSearch/startImport", async ({extendedSearchRunId, projectId}, {rejectWithValue}) => {
	const response = await extendedSearchApi.startImport(extendedSearchRunId, {
		project_id: projectId,
	});

	if (isFailure(response)) {
		// A 409 is a normal, expected outcome, not a crash: another run already holds the import
		// gate and the message names it. A 400 naming a project with no target list is the other
		// expected refusal. Carried through with its status so the page can show the operator
		// exactly which run to wait for, or which project cannot receive anybody.
		return rejectWithValue(errorFrom(response, "Could not schedule the import"));
	}

	// 200 means scheduled, not started. The atomic claim happens later, when the Cloud Function
	// dequeues the task, so the caller still has to poll the run to see what followed.
	return response.data;
});

export const abortExtendedSearchRun = createAsyncThunk<
	TExtendedSearchAbortResponse,
	number,
	TThunkConfig
>("extendedSearch/abortRun", async (extendedSearchRunId, {dispatch, rejectWithValue}) => {
	const response = await extendedSearchApi.abortRun(extendedSearchRunId);

	if (isFailure(response)) {
		// A 409 is an expected outcome, not a crash: the run has already reached Importing, where
		// the per-profile money is committed. The server explains that itself, so its wording is
		// what reaches the page.
		return rejectWithValue(errorFrom(response, "Could not abort this Extended Search run"));
	}

	// The response carries the new status, but the run is what the page reads a status from, so
	// this refreshes both rather than trusting a field the contract does not name.
	dispatch(fetchExtendedSearchRuns());
	dispatch(fetchExtendedSearchRun(extendedSearchRunId));

	return response.data;
});

/**
 * A project's company target list. Fetched on demand by the run form, never on mount: a project
 * has to be named first, and nothing else on the page uses it.
 */
export const fetchExtendedSearchProjectCompanies = createAsyncThunk<
	TExtendedSearchProjectCompaniesResponse,
	number,
	TThunkConfig
>("extendedSearch/fetchProjectCompanies", async (projectId, {rejectWithValue}) => {
	const response = await extendedSearchApi.getProjectCompanies(projectId);

	if (isFailure(response)) {
		// A 404 means the project has no target list, which is an answer the operator needs to
		// read rather than a failure to report as one.
		return rejectWithValue(
			errorFrom(response, "Could not load this project's company target list"),
		);
	}

	return response.data;
});

/**
 * Resolve a project id to its name, for the import picker. Fetched on demand, one project at a
 * time, as the operator finishes typing a number.
 *
 * A 404 is an answer, not a failure: the project does not exist, which is what an operator who
 * mistyped a digit needs to be told before they spend anything.
 */
export const fetchExtendedSearchImportProject = createAsyncThunk<
	TExtendedSearchImportProject,
	number,
	TThunkConfig
>("extendedSearch/fetchImportProject", async (projectId, {rejectWithValue}) => {
	const response = await extendedSearchApi.getImportProject(projectId);

	if (isFailure(response)) {
		return rejectWithValue(errorFrom(response, `Could not find project ${projectId}`));
	}

	return response.data;
});

/**
 * Projects matching what the operator typed, for the import picker.
 *
 * Goes through projectApi rather than a new Extended Search endpoint: /mapx/projects already
 * filters by name, paginates, and — the reason this needs no backend at all — returns
 * target_list_id, which is what says whether a project can receive anybody.
 *
 * Scoped to what the user can see, because that endpoint is. An operator cannot import into a
 * project they have no access to, which is a better answer than the id field gave: that one
 * resolved any id in the database.
 */
export const searchExtendedSearchProjects = createAsyncThunk<
	TExtendedSearchProjectMatch[],
	string,
	TThunkConfig
>("extendedSearch/searchProjects", async (name, {rejectWithValue}) => {
	const response = await projectApi.getProjects(
		{page: FIRST_PROJECT_PAGE, per_page: PROJECT_MATCH_LIMIT},
		name,
	);

	if (isFailure(response)) {
		return rejectWithValue(errorFrom(response, "Could not search projects"));
	}

	return (response.data as {results?: TExtendedSearchProjectMatch[]})?.results ?? [];
});
